import React, { useState } from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Prompt from "../Prompt/Prompt";
import PttPromptStyle from "../Prompt/PttPromptStyle";
import { createUseStyles } from "react-jss";
import { faMicrophone } from "@fortawesome/free-solid-svg-icons/faMicrophone";
import { isMobile } from "../../../lib/utils";
import { makeStylesFromThemeAndCustomStyles } from "../../../lib/StyleFunctions";
import widgetIconStyles from "../../Widgets/ClosedWidget/WidgetIconStyles";

const makeStyles = (theme) => ({
  recordButton: {
    composes: "$widgetIconBase",
    touchAction: "manipulation",
    position: "initial",
    margin: "5px 0 0 15px",
    innerIcon: { display: "block", margin: "0 auto" },
    "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
      {
        fontSize: "18px",
      },
  },
  recording: {
    composes: "$recordButton",
    animationName: "$shadowPulse",
    animationDuration: "1.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: "linear",
    "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
      {
        animationName: "$scale-up-center",
        animationDuration: "0.4s",
        animationIterationCount: 1,
        animationTimingFunction: "cubic-bezier(0.39, 0.575, 0.565, 1)",
        animationFillMode: "both",
      },
  },
  greyOutButton: {
    composes: "$recordButton",
    opacity: "0.6",
    filter: "grayscale(100%)",
    pointerEvents: "none",
  },
  "@keyframes shadowPulse": {
    "0%": { boxShadow: "0 0 0 0px rgba(0, 0, 0, 0.2)" },
    "100%": { boxShadow: "0 0 0 35px rgba(0, 0, 0, 0)" },
  },
  "@keyframes scale-up-center": {
    "0%": { WebkitTransform: "scale(1)", transform: "scale(1)" },
    "80%": { WebkitTransform: "scale(1.9)", transform: "scale(1.9)" },
    "100%": { WebkitTransform: "scale(1.75)", transform: "scale(1.75)" },
  },
  footerInterface: {
    width: "100%",
    padding: "5px 18px",
    boxSizing: "border-box !important",
    zIndex: "2147483700",
    fontSize: "1em",
    backgroundColor: theme.lightWhite,
  },
  sendIcon: {
    fill: "white",
    width: "26px",
    height: "26px",
    margin: "0 auto",
    display: "block",
  },
  fontAwesomeButton: {
    cursor: "pointer",
    color: theme.primaryColor,
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
  footerButtons: {
    cursor: "pointer",
    color: theme.primaryColor,
    backgroundColor: "transparent",
    borderColor: "transparent",
    fontSize: "133%",
  },
  floating: {
    marginRight: "12px",
    marginBottom: "7px",
    cssFloat: "right",
  },
  keyboardInput: {
    width: "100%",
    display: "flex",
  },
  inputGroup: { width: "84%" },
  characterCounter: {
    textAlign: "left",
    paddingLeft: "3%",
    fontStyle: "normal",
    fontWeight: "normal",
    fontSize: "11px",
    lineHeight: "16px",
    color: "rgba(53, 53, 53, 0.7)",
  },
  textInput: {
    display: "block",
    width: "100%",
    lineHeight: "24px",
    color: theme.textColor,
    backgroundClip: "padding-box",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
    fontWeight: "400",
    borderRadius: "30px",
    margin: "5px 0 13px 0",
    border: "0",
    resize: "none",
    boxSizing: "border-box",
    backgroundColor: "white",
    height: "50px",
    maxHeight: "100px",
    overflow: "auto",
    padding: "13px 13px 13px 18px",
  },
  ".textInput::placeholder": { fontSize: "13px" },
  "@media only screen and (max-width: 600px), only screen and (max-height: 450px)":
    {
      formControl: { fontSize: "14px" },
    },
  hints: { fontSize: "11px", margin: "5px 10px", color: "gray" },
});

const isSecure = () =>
  window.location.protocol === "https:" ||
  process.env.REACT_APP_ENV !== "production";

const canVibrate = "vibrate" in navigator || "mozVibrate" in navigator;

const maxInputLength = 140;

const RemainingCharactersCounter = ({
  maxChars = maxInputLength,
  usedChars,
  showThreshold = 30,
  styles,
}) => {
  const remaining = maxChars - usedChars;
  return remaining > showThreshold ? null : (
    <div className={styles.characterCounter}>
      <span style={remaining ? {} : { color: "black" }}>{remaining}</span>{" "}
      characters left
    </div>
  );
};

const Footer = ({
  hints,
  isRecording,
  textAreaPlaceholder,
  shouldBlock,
  stopRecording,
  startRecording,
  sendTextQuery,
  customWidgetStyle,
}) => {
  const showHints = hints && hints.length;
  const [showNotification, setShowNotification] = useState(false);
  const [onTouchEndTimer, setOnTouchEndTimer] = useState();
  const [value, setValue] = useState("");
  const [startTime, setStartTime] = useState();
  const useStyles = createUseStyles((theme) => ({
    ...widgetIconStyles(theme),
    ...makeStylesFromThemeAndCustomStyles(makeStyles, customWidgetStyle)(theme),
  }));
  const styles = useStyles();

  return (
    <div className={styles.footerInterface}>
      <RemainingCharactersCounter
        styles={styles}
        maxChars={maxInputLength}
        usedChars={value.length}
      />
      <div className={styles.keyboardInput} id={"hyro-keyboard-input"}>
        <label htmlFor="hyro-text-input" style={{ fontSize: "0" }}>
          Enter your question to the virtual assistant
        </label>
        <div className={styles.inputGroup}>
          <textarea
            style={showHints ? { marginBottom: 0 } : {}}
            className={styles.textInput}
            id={"hyro-text-input"}
            placeholder={textAreaPlaceholder || ""}
            value={value}
            maxLength={maxInputLength}
            onChange={(event) => setValue(event.target.value.slice(0, 250))}
            onKeyDown={(event) => {
              if (event.key === "Enter" && event.shiftKey === false) {
                event.preventDefault();
                if (!shouldBlock && event.target.value.trim()) {
                  setValue("");
                  sendTextQuery(event.target.value);
                }
              }
            }}
          />
        </div>
        {value || !isSecure() ? (
          <button
            className={shouldBlock ? styles.greyOutButton : styles.recordButton}
            onClick={() => {
              if (!shouldBlock && value.trim()) {
                setValue("");
                sendTextQuery(value);
              }
            }}
            aria-label="Submit"
            title="Submit"
          >
            <svg
              className={styles.sendIcon}
              id="hyro-send-icon"
              height="48"
              viewBox="0 0 48 48"
              width="48"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4.02 42L46 24 4.02 6 4 20l30 4-30 4z" />
              <path d="M0 0h48v48H0z" fill="none" />
            </svg>
          </button>
        ) : (
          <>
            {showNotification ? (
              <Prompt
                text="Hold to speak, release to stop"
                style={PttPromptStyle}
                customWidgetStyle={customWidgetStyle}
              />
            ) : null}
            <button
              className={
                shouldBlock
                  ? styles.greyOutButton
                  : isRecording
                  ? styles.recording
                  : styles.recordButton
              }
              id={Footer.SEND_OR_RECORD_ID}
              onClick={() => {
                if (isMobile()) {
                  return;
                }
                if (isRecording) {
                  stopRecording();
                } else {
                  startRecording();
                }
              }}
              onTouchStart={() => {
                startRecording();
                if (canVibrate) {
                  window.navigator.vibrate(100);
                }
                clearTimeout(onTouchEndTimer);
                setStartTime(new Date().getTime());
              }}
              onTouchEnd={(event) => {
                event.preventDefault();
                if (new Date().getTime() - startTime < 750) {
                  setShowNotification(true);
                  clearTimeout(onTouchEndTimer);
                  setOnTouchEndTimer(
                    setTimeout(() => {
                      setShowNotification(false);
                    }, 2000)
                  );
                } else {
                  setShowNotification(false);
                }
                stopRecording();
              }}
              aria-label="Click this button to interact with the voice assistant"
              title="Activate voice button"
            >
              <FontAwesomeIcon
                className={styles.innerIcon}
                aria-hidden="true"
                icon={faMicrophone}
              />
            </button>
          </>
        )}
      </div>
      {showHints ? (
        <div className={styles.hints}>
          {hints[0].length > 65 ? hints[0].slice(0, 65) + "..." : hints[0]}
        </div>
      ) : null}
    </div>
  );
};

Footer.SEND_OR_RECORD_ID = "hyro-record-button";

export default Footer;
